import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Button } from '../components/ui/button';
import {
  ChevronLeft, Shield, UserCheck, CreditCard, Camera, Scale,
  Handshake, Eye, HelpCircle, CheckCircle2, ArrowRight, Clock
} from 'lucide-react';
import HireHubLogo from '../components/HireHubLogo';
import { usePaymentMode } from '../contexts/PaymentModeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Section = ({ icon: Icon, title, color, children }) => (
  <div className="bg-white rounded-xl border border-slate-100 p-6 sm:p-8" data-testid={`section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="flex items-start gap-4 mb-4">
      <div className={`shrink-0 w-10 h-10 sm:w-11 sm:h-11 rounded-lg ${color} flex items-center justify-center`}>
        <Icon className="w-5 h-5 sm:w-5.5 sm:h-5.5 text-white" />
      </div>
      <h2 className="text-lg sm:text-xl font-bold text-slate-900 pt-1.5">{title}</h2>
    </div>
    <div className="text-sm sm:text-base text-slate-600 leading-relaxed space-y-3 pl-0 sm:pl-[60px]">
      {children}
    </div>
  </div>
);

const Check = ({ children }) => (
  <div className="flex items-start gap-2.5">
    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
    <span>{children}</span>
  </div>
);

export default function TrustSafetyPage() {
  const [meta, setMeta] = useState(null);
  const { isHostDirect } = usePaymentMode();

  useEffect(() => {
    axios.get(`${API_URL}/api/legal/trust-safety`)
      .then(r => setMeta(r.data))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Trust & Safety | HireHub Australia</title>
        <meta name="description" content="Learn how HireHub protects renters and hosts with verified users, secure bookings, and evidence-based dispute handling." />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="h-9 w-9 sm:h-10 sm:w-10">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <Link to="/">
              <HireHubLogo size="md" />
            </Link>
          </div>
          <Link to="/vehicles">
            <Button variant="outline" size="sm" className="h-9 text-sm">
              Browse Vehicles
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#004B8D] to-[#003366] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Trust & Safety</span>
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-4" data-testid="trust-hero-title">
            Trust & Safety at HireHub
          </h1>
          <p className="text-base sm:text-lg text-white/80 max-w-2xl mx-auto leading-relaxed">
            How we protect renters, hosts, and every booking.
          </p>
          <p className="text-sm sm:text-base text-white/60 max-w-xl mx-auto mt-4 leading-relaxed">
            HireHub replaces "hope it works out" with structured, trackable, and enforceable processes so every hire is safe for both sides.
          </p>
          {meta?.published_at && (
            <p className="text-xs text-white/40 mt-5 flex items-center justify-center gap-1.5" data-testid="last-updated">
              <Clock className="w-3 h-3" />
              Last updated: {new Date(meta.published_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
              {meta.version_id && <span className="ml-1">({meta.version_id})</span>}
            </p>
          )}
        </div>
      </section>

      {/* Sections */}
      <section className="py-10 sm:py-14">
        <div className="max-w-3xl mx-auto px-4 space-y-5">

          {/* 1. Verified Users */}
          <Section icon={UserCheck} title="Verified Users" color="bg-blue-600">
            <p>
              Every renter and host on HireHub goes through identity verification before they can book or list a vehicle. This isn't optional — it's how we keep the community safe.
            </p>
            <div className="space-y-1.5 pt-1">
              <Check>Valid driver's licence uploaded and reviewed</Check>
              <Check>Selfie photo matched against licence</Check>
              <Check>Manual admin review of all documents</Check>
              <Check>Accounts remain restricted until verification is approved</Check>
            </div>
            <p className="text-slate-500 text-sm pt-1">
              This means you always know who you're hiring from — or who's driving your vehicle.
            </p>
          </Section>

          {/* 2. Secure Bookings & Payments */}
          <Section
            icon={CreditCard}
            title={isHostDirect ? 'Structured Bookings & Payment Workflow' : 'Secure Bookings & Payments'}
            color="bg-emerald-600"
          >
            <p>
              Bookings on HireHub follow a structured flow that protects both parties. No cash handshakes, no guesswork.
            </p>
            <div className="space-y-1.5 pt-1">
              <Check>Request, approval, then payment — every booking follows the same steps</Check>
              {isHostDirect ? (
                <>
                  <Check>Payments are arranged directly between the renter and host (e.g. PayID, bank transfer, or agreed method). HireHub does not process or hold funds in this mode.</Check>
                  <Check>HireHub does not hold funds. The host confirms receipt of payment within HireHub so both sides share a timestamped record.</Check>
                  <Check>Security bonds are collected and recorded directly by the host and tracked within HireHub for transparency.</Check>
                </>
              ) : (
                <>
                  <Check>Payments processed securely through our trusted payment partner</Check>
                  <Check>Funds held by the platform until the hire completes</Check>
                  <Check>Security bonds authorised upfront and released or settled fairly</Check>
                </>
              )}
            </div>
            {isHostDirect ? (
              <p className="text-slate-500 text-sm pt-1">
                All payments are arranged directly with your host. Use only the payment details provided by your host through the booking — do not send money to anyone else who contacts you.
              </p>
            ) : (
              <p className="text-slate-500 text-sm pt-1">
                You'll never be asked to transfer money outside the platform. If someone asks you to, report it immediately.
              </p>
            )}
          </Section>

          {/* 3. Pre & Post Hire Inspections */}
          <Section icon={Camera} title="Pre & Post Hire Inspections" color="bg-violet-600">
            <p>
              Every hire includes structured photo checklists that document the vehicle's condition before and after the trip.
            </p>
            <div className="space-y-1.5 pt-1">
              <Check>Hosts complete a pre-hire checklist with timestamped photos</Check>
              <Check>Renters confirm the vehicle condition at pickup</Check>
              <Check>Post-hire inspection documents the return state</Check>
              <Check>All evidence is stored securely and tied to the booking</Check>
            </div>
            <p className="text-slate-500 text-sm pt-1">
              If a disagreement arises, there's a clear photographic record — not just someone's word.
            </p>
          </Section>

          {/* 4. Damage & Dispute Handling */}
          <Section icon={Scale} title="Damage & Dispute Handling" color="bg-amber-600">
            <p>
              When issues arise, HireHub provides a fair and evidence-based resolution process rather than leaving it to the parties to sort out alone.
            </p>
            <div className="space-y-1.5 pt-1">
              <Check>Cooldown period after hire for hosts to review and report issues</Check>
              <Check>Dispute window with structured evidence submission</Check>
              <Check>Admin-reviewed decisions based on inspection records and documentation</Check>
              {isHostDirect ? (
                <Check>Bond settlements are arranged directly between host and renter — HireHub provides evidence, messaging logs, and booking records for transparency.</Check>
              ) : (
                <Check>Bond settlements processed through the platform — transparent and auditable</Check>
              )}
            </div>
          </Section>

          {/* 5. Host & Renter Responsibilities */}
          <Section icon={Handshake} title="Host & Renter Responsibilities" color="bg-rose-600">
            <p>
              A good experience on HireHub depends on both sides acting in good faith. Here's what we expect:
            </p>
            <div className="grid sm:grid-cols-2 gap-4 pt-1">
              <div className="bg-slate-50 rounded-lg p-4 space-y-1.5">
                <p className="font-semibold text-slate-800 text-sm">Hosts</p>
                <Check>Provide accurate vehicle listings and photos</Check>
                <Check>Complete pre and post hire checklists honestly</Check>
                <Check>Communicate availability and rules clearly</Check>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 space-y-1.5">
                <p className="font-semibold text-slate-800 text-sm">Renters</p>
                <Check>Verify the vehicle at pickup and report issues immediately</Check>
                <Check>Treat hired vehicles with care</Check>
                <Check>Return on time and in the agreed condition</Check>
              </div>
            </div>
          </Section>

          {/* 6. Platform Oversight */}
          <Section icon={Eye} title="Platform Oversight" color="bg-slate-700">
            <p>
              HireHub isn't a hands-off marketplace. Our team actively monitors activity and steps in when needed to maintain standards.
            </p>
            <div className="space-y-1.5 pt-1">
              <Check>Admin monitoring of bookings, disputes, and user behaviour</Check>
              <Check>Comprehensive audit logging of all platform actions</Check>
              <Check>Ability to suspend or restrict accounts that violate policies</Check>
              <Check>Regular review of flagged transactions and escalated issues</Check>
            </div>
          </Section>

        </div>
      </section>

      {/* CTA */}
      <section className="py-10 sm:py-14 bg-white border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <HelpCircle className="w-10 h-10 text-[#004B8D] mx-auto mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">Still have questions?</h2>
          <p className="text-slate-600 text-sm sm:text-base mb-6">
            Visit our Contact page or reach out to our support team — we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link to="/contact">
              <Button className="bg-[#004B8D] hover:bg-[#003a6d] text-white px-6" data-testid="cta-contact">
                Contact Support <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/vehicles">
              <Button variant="outline" className="px-6" data-testid="cta-browse">
                Browse Vehicles
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="py-8 px-4 bg-slate-900 text-slate-400 text-center text-xs sm:text-sm">
        <p>&copy; {new Date().getFullYear()} HireHub Australia. All rights reserved.</p>
        <div className="flex items-center justify-center gap-4 mt-3 text-slate-500">
          <Link to="/legal/terms" className="hover:text-white transition-colors">Terms</Link>
          <Link to="/legal/privacy" className="hover:text-white transition-colors">Privacy</Link>
          <Link to="/contact" className="hover:text-white transition-colors">Contact</Link>
        </div>
      </footer>
    </div>
  );
}
